
import { useGLTF, Sphere } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { toast } from "@/hooks/use-toast";
import { MODEL_PATHS, preloadModels } from "./SceneUtils";

interface FootballBallProps {
  position: [number, number, number];
  enhancedModel?: boolean;
}

export const FootballBall = ({ position, enhancedModel = false }: FootballBallProps) => {
  const ballRef = useRef<THREE.Group>(null);
  const [modelError, setModelError] = useState(false);
  
  // Preload models when the component mounts
  useEffect(() => {
    console.log("Football Ball component mounted with enhanced model:", enhancedModel);
    preloadModels();
  }, [enhancedModel]);
  
  try {
    const { scene } = useGLTF(MODEL_PATHS.ball);
    console.log("Ball model loaded successfully");
    
    // Apply enhanced materials if requested
    useEffect(() => {
      if (enhancedModel && scene) {
        console.log("Applying enhanced materials to ball model");
        scene.traverse((obj) => {
          if (obj instanceof THREE.Mesh) {
            if (obj.material) {
              // Apply better materials for enhanced rendering
              obj.material.roughness = 0.3;
              obj.material.metalness = 0.1;
              obj.material.envMapIntensity = 1.5;
              obj.material.needsUpdate = true;
            }
          }
        });
      }
    }, [enhancedModel, scene]);
    
    useFrame((state) => {
      if (!ballRef.current) return;
      // More subtle animation for enhanced mode
      const bounceFactor = enhancedModel ? 0.05 : 0.1;
      const rotationSpeed = enhancedModel ? 0.005 : 0.01;
      
      ballRef.current.position.y = position[1] + Math.sin(state.clock.getElapsedTime() * 2) * bounceFactor;
      ballRef.current.rotation.y += rotationSpeed;
    });

    return (
      <group 
        ref={ballRef} 
        position={position} 
        scale={[0.3, 0.3, 0.3]}
        castShadow
      >
        <primitive object={scene} />
      </group>
    );
  } catch (error) {
    console.error("Failed to load ball model:", error);
    if (!modelError) {
      setModelError(true);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load ball model. Using fallback.",
      });
    }
    // Fallback to a simple sphere with enhanced materials if requested
    return (
      <group ref={ballRef} position={position}>
        <Sphere args={[0.5, enhancedModel ? 32 : 16, enhancedModel ? 32 : 16]} castShadow>
          <meshStandardMaterial 
            color="#ffffff" 
            roughness={enhancedModel ? 0.3 : 0.8}
            metalness={enhancedModel ? 0.2 : 0}
            envMapIntensity={enhancedModel ? 1.5 : 1}
          />
        </Sphere>
      </group>
    );
  }
};
